import React, { FC, SyntheticEvent } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput/ControlledTextInput'
import { Checkbox } from 'transfix-ui/components/Checkbox'
import Box from 'transfix-ui/core/Box'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { RefreshNormFileResponse, useRefreshNormFile } from 'apps/marketplace/apiTypes/instantRfp'
import {
  RefreshNormFileSchema,
  IRefreshNormFileSchema,
} from 'apps/marketplace/pages/instant-rfp/schema/RefreshNormFileSchema'
import { FormModal } from 'transfix-ui'
import { getUserCookieValues } from 'utils/userAuth'

const { userEmail } = getUserCookieValues()

interface IRefreshNormFileProps {
  onSuccess: (toastMessage: string) => void
  onError: (toastMessage: string) => void
  onClose: () => void
}

const normModalStyles = makeStyles(theme => ({
  checkbox: {
    marginLeft: theme.spacing(0.25),
  },
}))

export const RefreshNormFileModal: FC<IRefreshNormFileProps> = ({
  onClose,
  onError,
  onSuccess,
}) => {
  const { loading, mutate: refreshNormFile } = useRefreshNormFile({})
  const methods = useForm({
    resolver: yupResolver(RefreshNormFileSchema),
    defaultValues: {
      googleSheetsUrl: '',
      email: userEmail,
      draftRound: false,
    },
  })
  const { control, errors, handleSubmit, watch } = methods
  const refreshNorm = async (formParams: IRefreshNormFileSchema) => {
    try {
      const response: RefreshNormFileResponse = await refreshNormFile({ rfp: formParams })
      onSuccess(response.message)
      onClose()
    } catch (err) {
      onError(err.data?.errors || 'An unknown error occurred')
    }
  }

  const values = watch(['googleSheetsUrl', 'email'])
  const requiredFieldsFilledOut = !!values.googleSheetsUrl && !!values.email

  return (
    <FormModal
      open
      title='Refresh Norm'
      size={5}
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          dataTest: 'refresh-norm-file-modal-dismiss-button',
          action: onClose,
        },
        {
          label: 'Refresh Norm',
          type: 'confirm',
          dataTest: 'refresh-norm-file-modal-submit-button',
          action: handleSubmit(refreshNorm),
          disabled: !requiredFieldsFilledOut || loading,
        },
      ]}>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='googleSheetsUrl'
          TextInputProps={{
            name: 'googleSheetsUrl',
            label: 'Google Sheets URL',
            placeholder: 'Enter URL',
            dataTest: 'google-sheets-url',
            invalid: !!errors.googleSheetsUrl,
            validationMessage: errors.googleSheetsUrl?.message,
            required: true,
          }}
        />
      </Box>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='email'
          TextInputProps={{
            name: 'email',
            type: 'email',
            label: 'Email',
            placeholder: 'Enter email',
            dataTest: 'email',
            invalid: !!errors.email,
            validationMessage: errors.email?.message,
            required: true,
          }}
        />
      </Box>
      <Controller
        name='draftRound'
        control={control}
        render={({ onChange, value }) => {
          const onChangeHandler = (val: SyntheticEvent<HTMLInputElement>) => {
            onChange(val.currentTarget.checked)
          }
          return (
            <Box className={normModalStyles().checkbox}>
              <Checkbox
                checked={value}
                onChange={onChangeHandler}
                label='New Pricing Round: This RFP has a new pricing round and a CIS workspace will get generated.'
                dataTest='draft-round'
              />
            </Box>
          )
        }}
      />
    </FormModal>
  )
}
